/**
 * ========================
 * COMMUNICATIONS · SMS MENU PROVIDER
 * ========================
 *
 * Menu for the SMS channel sub-app (`/admin/communications/sms/*`).
 * `comms-sms` is a `hidden` domain reached from the Communications hub.
 * Visibility is permission-driven (Communications.SMS.*); Admins bypass.
 */

import {
  IconLayoutDashboard,
  IconPencil,
  IconUsersGroup,
  IconSend,
  IconTemplate,
  IconSettings,
  IconSpeakerphone,
} from '@tabler/icons-react';
import { DomainMenuProvider, MenuSection, DomainMenuConfig } from '../../layouts/menu/types';
import { createVisibilityCheck } from './BaseMenuProvider';
import { PERMISSIONS } from '../../config/permissions';

const P = PERMISSIONS.COMMUNICATIONS.SMS;
const BASE = '/admin/communications/sms';

class CommsSmsMenuProvider implements DomainMenuProvider {
  domainId = 'comms-sms';

  getMenuConfig(): DomainMenuConfig {
    const view = createVisibilityCheck({ permissions: [P.VIEW] });
    const send = createVisibilityCheck({ permissions: [P.SEND] });
    const manage = createVisibilityCheck({ permissions: [P.MANAGE] });
    const settings = createVisibilityCheck({ permissions: [P.SETTINGS] });
    const campaigns = createVisibilityCheck({ permissions: [PERMISSIONS.COMMUNICATIONS.CAMPAIGNS.VIEW] });

    const sections: MenuSection[] = [
      {
        id: 'comms-sms-overview',
        label: 'Overview',
        visible: view,
        items: [
          { id: 'comms-sms-dashboard', label: 'Dashboard', path: BASE, icon: <IconLayoutDashboard size={16} />, visible: view },
        ],
      },
      {
        id: 'comms-sms-send',
        label: 'Send',
        visible: send,
        items: [
          { id: 'comms-sms-compose', label: 'Compose', path: `${BASE}/compose`, icon: <IconPencil size={16} />, visible: send },
          { id: 'comms-sms-bulk', label: 'Bulk Send', path: `${BASE}/bulk`, icon: <IconUsersGroup size={16} />, visible: send },
        ],
      },
      {
        id: 'comms-sms-messages',
        label: 'Messages',
        visible: view,
        items: [
          { id: 'comms-sms-outbox', label: 'Outbox', path: `${BASE}/outbox`, icon: <IconSend size={16} />, visible: view },
          { id: 'comms-sms-campaigns', label: 'Campaigns', path: `${BASE}/campaigns`, icon: <IconSpeakerphone size={16} />, visible: campaigns },
        ],
      },
      {
        id: 'comms-sms-library',
        label: 'Library',
        visible: manage,
        items: [
          { id: 'comms-sms-templates', label: 'Templates', path: `${BASE}/templates`, icon: <IconTemplate size={16} />, visible: manage },
        ],
      },
      {
        id: 'comms-sms-config',
        label: 'Configuration',
        visible: settings,
        items: [
          { id: 'comms-sms-settings', label: 'Settings', path: `${BASE}/settings`, icon: <IconSettings size={16} />, visible: settings },
        ],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: { version: '1.0.0', lastUpdated: Date.now(), description: 'SMS channel — admin console' },
    };
  }
}

export const commsSmsMenuProvider = new CommsSmsMenuProvider();
